import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { CourtAvailabilityDto } from '../../../models/court/dto/court-availability.dto';
import { CourtAvailabilityTemplateDto } from '../../../models/court/dto/court-availability-template.dto';
import {
    CreateCourtFormAvailabilityInputType,
    MINUTES_PER_SLOT,
} from '../../../models/court/dto/court-availability-template.constants';
import { SportType } from '../../../models/court/dto/court.dto';
import { WILDCARD_CAPACITY } from '../../../utils/sport-capacity';
import { seedAvailabilityFormState } from '../../court-management/utils/seed-availability-form-state';
import {
    CLEARED_CELL,
    PriceOverrides,
    PricingBlock,
    PricingGridState,
    flattenBlocksToSlots,
    formatMinutes,
    makeDefaultBlock,
    priceOverrideKey,
} from '../model';

export type GridSeed = {
    state: PricingGridState;
    isGameAvailable: boolean;
    isCoachingAvailable: boolean;
    returnAsOneChunk: boolean;
    currency: Currency;
    /** See `seedAvailabilityFormState.hasMisencodedRules`. */
    hasMisencodedRules: boolean;
};

const MS_PER_MINUTE = 60_000;

/** One template rule -> one block. Rules that only differ by weekday collapse together. */
function blocksFromRules(rules: CreateCourtFormAvailabilityInputType[], makeId: () => string): PricingBlock[] {
    const byShape = new Map<string, PricingBlock>();
    for (const rule of rules) {
        const durationMinutes = Math.round(
            (rule.configurations[0]?.slotDurationInMs ?? rule.slotDurationInMs) / MS_PER_MINUTE,
        );
        const start = rule.min * MINUTES_PER_SLOT;
        const end = rule.max * MINUTES_PER_SLOT;
        const prices = new Set(rule.configurations.map((c) => c.price).filter((p) => typeof p === 'number'));
        const key = `${start}|${end}|${durationMinutes}`;
        if (byShape.has(key)) continue;
        byShape.set(key, {
            id: makeId(),
            start: formatMinutes(start),
            end: formatMinutes(end),
            durationMinutes: durationMinutes > 0 ? durationMinutes : 60,
            intervalMinutes: durationMinutes > 0 ? durationMinutes : 60,
            priceRaw: prices.size === 1 && rule.configurations.every((c) => typeof c.price === 'number')
                ? String(Array.from(prices)[0])
                : '',
        });
    }
    return Array.from(byShape.values());
}

type LocalRow = {
    day: number;
    startMinute: number;
    endMinute: number;
    pricings: CourtAvailabilityDto['pricings'];
};

function toLocalRow(availability: CourtAvailabilityDto): LocalRow {
    const start = new Date(availability.startTime);
    const end = new Date(availability.endTime);
    const startMinute = start.getHours() * 60 + start.getMinutes();
    return {
        day: start.getDay(),
        startMinute,
        endMinute: startMinute + Math.round((end.getTime() - start.getTime()) / MS_PER_MINUTE),
        pricings: availability.pricings ?? [],
    };
}

/**
 * The interval a block was saved with cannot be read back from the template
 * (it is not a persisted field), so it is inferred from the live rows: the
 * smallest gap between two slot starts of the block's duration inside the
 * block's hours. Falls back to the duration when the week holds fewer than
 * two such slots.
 */
function inferIntervals(blocks: PricingBlock[], rows: LocalRow[]): PricingBlock[] {
    return blocks.map((block) => {
        const start = Number(block.start.slice(0, 2)) * 60 + Number(block.start.slice(3, 5));
        const end = Number(block.end.slice(0, 2)) * 60 + Number(block.end.slice(3, 5));
        const durationMs = block.durationMinutes * MS_PER_MINUTE;
        const startsByDay = new Map<number, Set<number>>();
        for (const row of rows) {
            if (row.startMinute < start || row.endMinute > end) continue;
            if (!row.pricings.some((p) => p.slotDurationInMs === durationMs)) continue;
            if (row.endMinute - row.startMinute !== block.durationMinutes) continue;
            if (!startsByDay.has(row.day)) startsByDay.set(row.day, new Set());
            startsByDay.get(row.day)!.add(row.startMinute);
        }
        let smallestGap = Infinity;
        startsByDay.forEach((starts) => {
            const sorted = Array.from(starts).sort((a, b) => a - b);
            for (let i = 1; i < sorted.length; i++) {
                smallestGap = Math.min(smallestGap, sorted[i] - sorted[i - 1]);
            }
        });
        return Number.isFinite(smallestGap) && smallestGap > 0
            ? { ...block, intervalMinutes: smallestGap }
            : block;
    });
}

/**
 * Per-cell prices from one week of live availabilities. A cell is priced
 * for a sport when some row of that weekday covers it entirely and carries
 * a pricing for that sport and the cell's duration. The wildcard-capacity
 * row is preferred — the grid has no capacity axis — and the first
 * capacity-specific row stands in when there is no wildcard.
 *
 * When the week has rows at all, every grid cell no row covers is marked
 * cleared, so the grid shows what is actually bookable rather than the
 * block's flat price.
 */
function overridesFromRows(
    blocks: PricingBlock[],
    rows: LocalRow[],
    sports: SportType[],
): PriceOverrides {
    const overrides: PriceOverrides = {};
    if (rows.length === 0) return overrides;
    const slots = flattenBlocksToSlots(blocks);
    for (const sport of sports) {
        for (let day = 0; day < 7; day++) {
            const dayRows = rows.filter((r) => r.day === day);
            for (const slot of slots) {
                const durationMs = (slot.endMinute - slot.startMinute) * MS_PER_MINUTE;
                let price: number | null = null;
                for (const row of dayRows) {
                    if (row.startMinute > slot.startMinute || row.endMinute < slot.endMinute) continue;
                    const candidates = row.pricings.filter(
                        (p) => p.sport === sport && p.slotDurationInMs === durationMs,
                    );
                    if (candidates.length === 0) continue;
                    const wildcard = candidates.find(
                        (p) => p.capacity === undefined || p.capacity === null || p.capacity === WILDCARD_CAPACITY,
                    );
                    price = (wildcard ?? candidates[0]).price;
                    break;
                }
                overrides[priceOverrideKey(sport, day, slot)] = price === null ? CLEARED_CELL : price;
            }
        }
    }
    return overrides;
}

/**
 * Everything the grid editor restores for an existing court: blocks from
 * the saved template (hours, duration, flat price), intervals and per-cell
 * prices from one week of live rows, and the three schedule flags.
 */
export function seedGridState(
    court: { sports?: SportType[]; availabilityTemplates?: CourtAvailabilityTemplateDto[] } | undefined,
    weekAvailabilities: CourtAvailabilityDto[],
    makeId: () => string,
): GridSeed {
    const base = seedAvailabilityFormState(court, () => ({
        id: makeId(),
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        min: 8 * 4,
        max: 22 * 4,
        slotDurationInMs: 60 * MS_PER_MINUTE,
        configurations: [],
    }));
    const template = court?.availabilityTemplates?.[0];
    const sports = court?.sports ?? [];
    const rows = weekAvailabilities.map(toLocalRow);

    const seededBlocks = template ? blocksFromRules(base.availabilities, makeId) : [];
    const blocks = inferIntervals(seededBlocks.length > 0 ? seededBlocks : [makeDefaultBlock(makeId())], rows);

    const currency =
        template?.rules.flatMap((r) => r.configurations).find((c) => c.currency)?.currency ??
        rows.flatMap((r) => r.pricings).find((p) => p.currency)?.currency ??
        Currency.EUR;

    return {
        state: { blocks, overrides: overridesFromRows(blocks, rows, sports) },
        isGameAvailable: base.isGameAvailable,
        isCoachingAvailable: base.isCoachingAvailable,
        returnAsOneChunk: base.returnAsOneChunk,
        currency,
        hasMisencodedRules: base.hasMisencodedRules,
    };
}
